import fahrzeugtyp from '@ahd/shared/maps/fahrzeugtyp';
import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import type { CarHistory } from '@ahd/shared/types/ahd-cars/car/history';
import type { CarPreisstaffel } from '@ahd/shared/types/ahd-cars/car/preis-staffel';
import type { AxiosRequestConfig } from 'axios';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { cloneDeep } from 'es-toolkit';
import sCols from '../_json/s_cols';
import axios from './Axios';
import { getPreisschilder } from './AxiosAI';
import { getAktBewertung } from './AxiosBewertung';
import { getMobileStatList, getPricerating } from './AxiosExport';
import { getScannerHist4IdCar } from './AxiosScanner';
import { getAxiosHeader, getVehicleLead, getVehiclesLead, urlExportRelease, urlRelease, urlServicesRelease } from './AxiosVars';
import { formatTimestamp, parseDate } from './Date';
import { getIdFirma, isAIPreisschilder, isBtoB, isImport2EVA } from './Session';
import { getAllFieldFromArray, getElementFromArray } from './Texte';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull, encodeDecodeURIComponent } from './Tools';
import { renderDataGridValue } from './datagrid/func';
import { type SelectDataObject } from './inputfields/Select';

export type AxiosGetCarlist = {
    cars?: Car[];
    del?: Car['id'][];
    facets?: {
        antrieb?: Pick<SelectDataObject, 'i' | 't'>[];
        attr?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_ASSISTENZSYSTEME?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_AUSSENAUSSTATTUNG?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_INNENAUSSTATTUNG?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_MULTIMEDIAENTERTAINMENT?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_PAKETE?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_RADERREIFEN?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_SCHEINWERFERLEUCHTEN?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_SITZEPOLSTER?: Pick<SelectDataObject, 'i' | 't'>[];
        attr_TECHNIKSICHERHEIT?: Pick<SelectDataObject, 'i' | 't'>[];
        aufbau?: Pick<SelectDataObject, 'i' | 't'>[];
        bereiche?: Pick<SelectDataObject, 'i' | 't'>[];
        bereicheAll?: Pick<SelectDataObject, 'i' | 't'>[];
        doors?: Pick<SelectDataObject, 'i' | 't'>[];
        ezBis?: Pick<SelectDataObject, 'i' | 't'>[];
        ezVon?: Pick<SelectDataObject, 'i' | 't'>[];
        farbe?: Pick<SelectDataObject, 'i' | 't'>[];
        firmen?: Pick<SelectDataObject, 'i' | 't'>[];
        getriebe?: Pick<SelectDataObject, 'i' | 't'>[];
        hersteller?: Pick<SelectDataObject, 'i' | 't'>[];
        kategorie?: Pick<SelectDataObject, 'i' | 't'>[];
        kmBis?: Pick<SelectDataObject, 'i' | 't'>[];
        kmVon?: Pick<SelectDataObject, 'i' | 't'>[];
        kraftstoff?: Pick<SelectDataObject, 'i' | 't'>[];
        leistungKWBis?: Pick<SelectDataObject, 'i' | 't'>[];
        leistungKWVon?: Pick<SelectDataObject, 'i' | 't'>[];
        leistungPSBis?: Pick<SelectDataObject, 'i' | 't'>[];
        leistungPSVon?: Pick<SelectDataObject, 'i' | 't'>[];
        modell?: Pick<SelectDataObject, 'i' | 't'>[];
        motor?: Pick<SelectDataObject, 'i' | 't'>[];
        preisBis?: Pick<SelectDataObject, 'i' | 't'>[];
        preisVon?: Pick<SelectDataObject, 'i' | 't'>[];
        sitze?: Pick<SelectDataObject, 'i' | 't'>[];
        standort?: Pick<SelectDataObject, 'i' | 't'>[];
        status?: Pick<SelectDataObject, 'i' | 't'>[];
        isCached?: boolean;
    };
    isCached?: boolean;
    searchResult?: Car['id'][];
    size?: {
        all?: number;
        [index: number]: number;
    };
    sizeAll?: number;
};

let controller: AbortController;

export async function improveCarList(data: Car[], selected: Car['id'][] | undefined, firmen: number[] | undefined): Promise<Car[]> {
    try {
        if (controller) controller.abort();
        controller = new AbortController();
        const newCarList = Object.assign([], data);
        const carIds: Car['id'][] = !checkUndefinedNullOrEmpty(selected) ? selected : getAllFieldFromArray(newCarList, 'id', []);

        let carExportData;
        try {
            carExportData = !checkUndefinedNullOrEmpty(firmen) ? await getVehiclesPortalBlacklist(firmen, controller.signal).then(({ data: dataPortalBlacklist }) => dataPortalBlacklist) : undefined;
        } catch (error: any) {
            console.error('Fehler bei improveCarList - carExportData:', error);
        }

        let carLeadData: { [index: Car['id']]: { offen: number; beendet: number } };
        try {
            carLeadData = await getVehiclesLead(carIds, controller.signal).then(({ data: dataVehicleLeads }) => {
                const newData = {};
                dataVehicleLeads.map((car) => {
                    newData[car.idCar] = {
                        offen: car?.leadsOffen?.leads?.length || 0,
                        beendet: car?.leadsClosed?.leads?.length || 0,
                    };
                });
                return newData;
            });
        } catch (error: any) {
            console.error('Fehler bei improveCarList - carLeadData:', error);
        }
        let carMobileTrackingData;
        try {
            carMobileTrackingData = await getMobileStatList(carIds, controller.signal).then(({ data: dataMobileStats }) => dataMobileStats);
        } catch (error: any) {
            console.error('Fehler bei improveCarList - carMobileTrackingData:', error);
        }
        let carPreisschildData;
        try {
            carPreisschildData =
                isAIPreisschilder() &&
                (await getPreisschilder(controller.signal).then(({ data: dataPreisschilder }) => {
                    const newData = Object.assign(
                        [],
                        dataPreisschilder?.data.filter((schild) => !checkUndefinedNullOrEmpty(schild.carId))
                    );
                    newData.map((preisschild) => {
                        if (preisschild.status === 'ok' || preisschild.status === 'OK') preisschild.status = 3;
                        else if (preisschild.status === 'sending' || preisschild.status === 'transferring' || preisschild.status === 'Wird gesendet...') preisschild.status = 2;
                        else if (preisschild.status === 'queued' || preisschild.status === 'In Warteschlange') preisschild.status = 1;
                        else preisschild.status = 0;
                    });
                    return newData;
                }));
        } catch (error: any) {
            console.error('Fehler bei improveCarList - carPreisschildData:', error);
        }
        let carTrackingData;
        try {
            carTrackingData = await getTrackingList(getIdFirma(), carIds, controller.signal).then(({ data: dataTracking }) => {
                const newData = Object.assign({}, dataTracking);
                Object.keys(newData).map((idCar) => (newData[idCar] = getElementFromArray(newData[idCar], 1, 'idPortal', undefined)));
                return newData;
            });
        } catch (error: any) {
            console.error('Fehler bei improveCarList - carTrackingData:', error);
        }

        for (let i = 0; i < newCarList.length; i++) {
            const newCar = Object.assign({}, await improveCar(newCarList[i], false, true));

            const carPreisschild = !checkUndefinedNullOrEmpty(carPreisschildData) ? getElementFromArray(carPreisschildData, newCar?.id, 'carId', undefined) : undefined;
            newCar.aip = !checkUndefinedNullOrEmpty(carPreisschild) ? carPreisschild.status : 0;

            const carExport = !checkUndefinedNullOrEmpty(carExportData) ? carExportData[newCar?.id] : undefined;
            if (!checkUndefinedNullOrEmpty(carExport)) {
                newCar.export = {};
                newCar.export.exp = carExport.export === true ? 2 : 1;
                newCar.export.err = !['Export deaktiviert', 'Falscher Standort'].includes(carExport.err) ? carExport.err : undefined;

                const portale = Object.keys(carExport).filter((key) => !['export', 'exp', 'err'].includes(key));
                portale.map((portalID) => {
                    newCar.export[portalID] = {
                        exp: carExport[portalID].ex === true ? 2 : 1,
                        err: !['Export deaktiviert', 'Falscher Standort'].includes(carExport[portalID].err) ? carExport[portalID].err : undefined,
                    };
                });
            } else newCar.export = { exp: 0 };

            const carLead = !checkUndefinedNullOrEmpty(carLeadData) ? carLeadData[newCar?.id] : undefined;
            newCar.leads = {
                offenAmount: !carLead?.offen || carLead.offen === 0 ? '-' : carLead.offen === 1 ? '1 Lead' : carLead.offen + ' Leads',
                beendetAmount: !carLead?.beendet || carLead.beendet === 0 ? '-' : carLead.beendet === 1 ? '1 Lead' : carLead.beendet + ' Leads',
            };

            const carTracking = !checkUndefinedNullOrEmpty(carTrackingData) ? carTrackingData[newCar?.id] : undefined;
            newCar.tracking = !checkUndefinedNullOrEmpty(carTracking) ? carTracking : {};

            const carMobileTracking = !checkUndefinedNullOrEmpty(carMobileTrackingData) ? carMobileTrackingData[newCar?.id] : undefined;
            newCar.tracking.pos = {
                ...newCar.tracking.pos,
                priceRange: !checkUndefinedNullOrEmpty(carMobileTracking) ? carMobileTracking.pr : 0,
                impressions: !checkUndefinedNullOrEmpty(carMobileTracking) ? carMobileTracking.i : 0,
                parkings: !checkUndefinedNullOrEmpty(carMobileTracking) ? carMobileTracking.p : 0,
                calls: !checkUndefinedNullOrEmpty(carMobileTracking) ? carMobileTracking.c : 0,
                mails: !checkUndefinedNullOrEmpty(carMobileTracking) ? carMobileTracking.e : 0,
            };
            newCarList[i] = newCar;
        }

        return newCarList;
    } catch (error: any) {
        console.error('Fehler bei improveCarList:', error);
        return data;
    }
}

export function getCarList(firmen, force, verkauft, suche, signal: AbortController['signal'], config?: AxiosRequestConfig) {
    const dataPathString: string[] = [];
    const dataBodyString: string[] = [];
    const dataPath = {
        ...suche,
        sold: verkauft ? 1 : undefined,
        virtuell: suche.virtuell ? 0 : undefined,
        filterAttr: 1,
    };

    let aus = [];
    if (dataPath.arTechnik) {
        aus = aus.concat(dataPath.arTechnik);
        delete dataPath.arTechnik;
    }
    if (dataPath.arAussen) {
        aus = aus.concat(dataPath.arAussen);
        delete dataPath.arAussen;
    }
    if (dataPath.arInnen) {
        aus = aus.concat(dataPath.arInnen);
        delete dataPath.arInnen;
    }
    dataPath.ia = aus.join('.');

    if (dataPath.SoldVon && dataPath.SoldBis) {
        const soldMonate = differenceInMonths(new Date(), parseDate(suche.SoldVon, 'T'));
        if (soldMonate > 12) dataPath.soldLimit = soldMonate;
    }

    if (dataPath.EZ) {
        dataPath.EZVon = dataPath.EZ[0] || 0;
        dataPath.EZBis = dataPath.EZ[1] || 0;
        delete dataPath.EZ;
    }
    if (dataPath.KM) {
        dataPath.KMVon = dataPath.KM[0] || 0;
        dataPath.KMBis = dataPath.KM[1] || 0;
        delete dataPath.KM;
    }
    if (dataPath.Leistung) {
        dataPath.LeistungVon = dataPath.Leistung[0] || 0;
        dataPath.LeistungBis = dataPath.Leistung[1] || 0;
        delete dataPath.Leistung;
    }
    if (dataPath.Preis) {
        dataPath.PreisVon = dataPath.Preis[0] || 0;
        dataPath.PreisBis = dataPath.Preis[1] || 0;
        delete dataPath.Preis;
    }

    for (let i = 0; i < Object.keys(dataPath).length; i++) {
        const key = Object.keys(dataPath)[i];
        const value = dataPath[key];
        if (!checkUndefinedNullOrEmpty(value) && value !== -100) dataPathString.push(key + '=' + value);
    }

    const dataBody: { firmen?: string; force: boolean | '' } = {
        firmen: isBtoB() ? firmen.join(',') : getIdFirma().toString(),
        force: force || '',
    };
    for (let i = 0; i < Object.keys(dataBody).length; i++) {
        const key = Object.keys(dataBody)[i];
        const value = dataBody[key];
        if (!checkUndefinedNullOrEmpty(value) && value !== -100) dataBodyString.push(key + '=' + value);
    }

    const url = urlServicesRelease + '/cars/v6/vehicles/facetten?' + dataPathString.join('&');
    return axios<AxiosGetCarlist>({
        title: 'Abrufen der Fahrzeugliste',
        method: 'post',
        url: url,
        data: dataBodyString.join('&'),
        headers: getAxiosHeader(config?.customToken),
        signal: signal,
        ...config,
    });
}

export function getHistory(idCar: Car['id'], idCol?: number) {
    const url = urlServicesRelease + '/cars/v1/vehicle/history/' + getIdFirma() + '/' + idCar + (idCol ? '/' + idCol : '');
    return axios<CarHistory[]>({
        title: 'Abrufen der Historie',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export async function improveCar(data: Car, sold: boolean, list: boolean): Promise<Car> {
    try {
        const newCar = cloneDeep(data);

        if (!checkUndefinedNullOrEmpty(newCar?.fin)) {
            if (checkUndefinedOrNull(newCar?.fd)) {
                newCar.fd = {};
            }
            newCar.fd.fin = newCar?.fin;
            delete newCar?.fin;
        }
        if (!checkUndefinedNullOrEmpty(newCar?.hi)) newCar.hi = encodeDecodeURIComponent(newCar?.hi, true);
        if (!checkUndefinedNullOrEmpty(newCar?.minAttr)) newCar.minAttr = encodeDecodeURIComponent(newCar?.minAttr, true);

        if (newCar?.tp && newCar?.tp.length > 1) {
            newCar.tp = fahrzeugtyp?.find((t) => t?.t === newCar?.tp)?.i;
        }

        if (list === true) {
            newCar.warnings = !checkUndefinedNullOrEmpty(newCar?.mmError)
                ? 3
                : !checkUndefinedNullOrEmpty(newCar?.mmWarn) || !checkUndefinedNullOrEmpty(newCar?.cObs?.aus) || !checkUndefinedNullOrEmpty(newCar?.warnMissingAttr)
                    ? 2
                    : !checkUndefinedNullOrEmpty(newCar?.mmHints)
                        ? 1
                        : 0;

            newCar.bildCount = 0;
            if (newCar?.imgs) {
                for (let j = 0; j < newCar?.imgs.length; j++) {
                    const bild = newCar?.imgs[j];
                    const ignore = [3, 7, 20, 21, 22, 30]; // 3: Füllbilder, 7: Placeholder, 20: 360Grad Innen, 21: Finanzierungsbild, 22: 360Grad Außen, 30: Schadensbilder
                    if (ignore.includes(bild.typ) || (bild.typ === 0 && bild.nr === 0)) continue; // 0: EyeCatcher beim ersten Bild
                    newCar.bildCount += 1;
                }
            }
            newCar.mobileTopinserat = false;
            newCar.mobileBlickfang = false;
            newCar.mobileSeiteEins = false;
            newCar.mobileRotstiftpreis = false;
            newCar.mobileAdTurbo = false;
            newCar.mobileEbayKleinanzeigen = false;
            newCar.aktionsfahrzeug = false;
            if (newCar?.lpf) {
                for (let j = 0; j < newCar?.lpf.length; j++) {
                    if (newCar?.lpf[j].aktiv) {
                        if (!newCar?.mobileTopinserat) newCar.mobileTopinserat = newCar?.lpf[j].typ === 4;
                        if (!newCar?.mobileBlickfang) newCar.mobileBlickfang = newCar?.lpf[j].typ === 3;
                        if (!newCar?.mobileSeiteEins) newCar.mobileSeiteEins = newCar?.lpf[j].typ === 2;
                        if (!newCar?.mobileRotstiftpreis) newCar.mobileRotstiftpreis = newCar?.lpf[j].typ === 10;
                        if (!newCar?.mobileAdTurbo) newCar.mobileAdTurbo = newCar?.lpf[j].typ === 12;
                        if (!newCar?.mobileEbayKleinanzeigen) newCar.mobileEbayKleinanzeigen = newCar?.lpf[j].typ === 11;
                        if (!newCar?.aktionsfahrzeug) newCar.aktionsfahrzeug = newCar?.lpf[j].typ === 1;
                    }
                }
            }
            newCar.stat = newCar?.stat || {};
            if (newCar?.lStatus && newCar?.lStatus.length > 1) {
                for (let j = 0; j < newCar?.lStatus.length; j++) {
                    switch (newCar?.lStatus[j].status) {
                        case 25:
                            newCar.stat.zg = newCar?.lStatus[j].datum;
                            break;
                        case 55:
                            newCar.stat.verfugbar = newCar?.lStatus[j].datum;
                            break;
                        case 85:
                            newCar.stat.sw = newCar?.lStatus[j].datum;
                            break;
                        case 100:
                            newCar.stat.zr = newCar?.lStatus[j].datum;
                            break;
                        case 105:
                            newCar.stat.ec = newCar?.lStatus[j].datum;
                            break;
                        case 115:
                            newCar.stat.w = newCar?.lStatus[j].datum;
                            break;
                        case 120:
                            newCar.stat.wok = newCar?.lStatus[j].datum;
                            break;
                        case 116:
                            newCar.stat.we = newCar?.lStatus[j].datum;
                            break;
                        case 121:
                            newCar.stat.weok = newCar?.lStatus[j].datum;
                            break;
                        case 125:
                            newCar.stat.l = newCar?.lStatus[j].datum;
                            break;
                        case 130:
                            newCar.stat.lok = newCar?.lStatus[j].datum;
                            break;
                        case 135:
                            newCar.stat.a = newCar?.lStatus[j].datum;
                            break;
                        case 140:
                            newCar.stat.aok = newCar?.lStatus[j].datum;
                            break;
                        case 150:
                            newCar.stat.vkf = newCar?.lStatus[j].datum;
                            break;
                        case 182:
                            newCar.stat.wv = newCar?.lStatus[j].datum;
                            break;
                        case 183:
                            newCar.stat.wvok = newCar?.lStatus[j].datum;
                            break;
                        case 184:
                            newCar.stat.tuv = newCar?.lStatus[j].datum;
                            break;
                        case 185:
                            newCar.stat.tuvok = newCar?.lStatus[j].datum;
                            break;
                    }
                }
            }
        }

        if (!checkUndefinedNullOrEmpty(newCar?.lb1) && newCar?.lb1?.id === 245) {
            delete newCar?.lb1;
        }

        if (!checkUndefinedNullOrEmpty(newCar?.lb2) && newCar?.lb2?.id === 245) {
            delete newCar?.lb2;
        }

        if (!list) {
            newCar.hist = await getScannerHist4IdCar(newCar?.id).then(({ data: dataHistory }) => {
                if (dataHistory) {
                    const carHist = dataHistory.filter((el) => el.status === 180 || el.status === 181).reverse();
                    const array = [];

                    carHist.map((element, index) => {
                        if (element.status === 180) array.push({ s: formatTimestamp(element.datum) });
                        else if (index === 0 && element.status === 181) array.push({ s: undefined, e: formatTimestamp(element.datum), km: element.nummer ? element.nummer + ' km' : undefined });
                        else if (element.status === 181 && !array[array.length - 1].e)
                            array[array.length - 1] = { ...array[array.length - 1], e: formatTimestamp(element.datum), km: element.nummer ? element.nummer + ' km' : undefined };
                    });

                    return array;
                }
                return undefined;
            });
        }
        if (isImport2EVA() && !list && !sold) {
            newCar.eva = await getEVAExport(newCar?.id).then(({ data: dataEVAExport }) => {
                return dataEVAExport[newCar?.id];
            });
        }
        if (!list && !sold) {
            const rawMobilePriceRange = (await getPricerating(newCar?.id))?.data;
            if (!checkUndefinedNullOrEmpty(rawMobilePriceRange?.labelRanges)) {
                newCar.priceRange = {
                    labelRanges: rawMobilePriceRange?.labelRanges?.map((l, i) => ({
                        color: ['success.darkest', 'success.main', 'success.lightest', 'warning.lightest', 'warning.main'][i],
                        from: parseFloat(l?.from),
                        label: ['Sehr gut', 'Gut', 'Fair', 'Erhöht', 'Hoch'][i],
                        to: parseFloat(l?.to),
                    })),
                };
            }
        }
        if (!list) {
            newCar.inz = await getAktBewertung(newCar?.id).then(({ data: dataBewertung }) => {
                if (!checkUndefinedNullOrEmpty(dataBewertung?.data)) {
                    const inz = JSON.parse(dataBewertung.data);
                    inz.i = dataBewertung.id;
                    inz.ic = dataBewertung.id_car;
                    return inz;
                }
                return undefined;
            });
        }
        if (!list) newCar.leads = (await getVehicleLead(newCar?.id))?.data;
        if (!list && !sold) {
            const leasingData = await getLeasing(newCar?.id).then(({ data: dataLeasing }) => {
                if (dataLeasing && !checkUndefinedNullOrEmpty(Object.keys(dataLeasing))) {
                    return dataLeasing;
                }
                return undefined;
            });
            if (!checkUndefinedNullOrEmpty(leasingData)) {
                if (checkUndefinedOrNull(newCar?.fd)) {
                    newCar.fd = {};
                }
                newCar.fd.leasing = leasingData;
            }
        }

        return newCar;
    } catch (error: any) {
        console.error('Fehler bei improveCar:', error);
        return data;
    }
}

export function improveCarHistory(data: CarHistory[], mwst?: Car['pr']['vt']) {
    const array = [];

    for (let i = 0; i < data.length; i++) {
        const origTo = data[i]?.to;
        const origTn = data[i]?.tn;
        const origIcc = data[i]?.icc;
        const row = getElementFromArray(sCols, origIcc, 'i', undefined);
        if (!checkUndefinedNullOrEmpty(row)) {
            const to = renderDataGridValue(undefined, origTo, row?.inputProps, {
                convertToNumber: true,
                factor: row?.faktor,
                mwst: mwst,
                parseDateFormat: typeof row?.parseDateFormat === 'object' ? row?.parseDateFormat.to : row?.parseDateFormat,
            });
            const tn = renderDataGridValue(undefined, origTn, row?.inputProps, {
                convertToNumber: true,
                factor: row?.faktor,
                mwst: mwst,
                parseDateFormat: typeof row?.parseDateFormat === 'object' ? row?.parseDateFormat.tn : row?.parseDateFormat,
            });
            array.push({
                i: data[i]?.i,
                d: data[i]?.d,
                to: to,
                tn: tn,
                field: row?.t,
                icc: origIcc,
                ui: data[i]?.ui,
            });
        } else {
            array.push({
                i: data[i]?.i,
                d: data[i]?.d,
                to: origTo,
                tn: origTn,
                field: 'unbekannt',
                icc: origIcc,
                ui: data[i]?.ui,
            });
        }
    }
    array.sort((a, b) => b.d - a.d);

    return array;
}

export function getStaffel(idCar: Car['id']) {
    const url = urlServicesRelease + '/cars/v1/staffelpreis/' + idCar;
    return axios<CarPreisstaffel[]>({
        title: 'Abrufen der Preisstaffel',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setStaffel(idCar: Car['id'], data: CarPreisstaffel) {
    const url = urlServicesRelease + '/cars/v1/staffelpreis/' + idCar;
    return axios({
        title: 'Speichern der Preisstaffel',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function resetToOriginal(idCar: Car['id'], idCol) {
    const url = urlServicesRelease + '/cars/v5/uweweb/car/' + idCar + '/original/' + idCol;
    return axios({
        title: 'Zurücksetzen',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

function getVehiclesPortalBlacklist(firmen: number[], signal: AbortController['signal']) {
    const url = urlServicesRelease + '/cars/v1/blacklist/firmen';
    return axios({
        title: 'Laden der Blacklists (Portale)',
        method: 'post',
        url: url,
        data: firmen.join(','),
        headers: getAxiosHeader(),
        signal: signal,
    });
}

export function setBlacklistForPortal(idCar: Car['id'], idPortal, newStatus: 0 | 1) {
    const url = urlServicesRelease + '/cars/v1/blacklist/' + getIdFirma() + '/' + idCar + '/' + idPortal + '/' + newStatus;
    return axios({
        title: newStatus === 0 ? 'Deaktivierung des Exportportals' : 'Aktivierung des Exportportals',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getLeasing(idCar: Car['id']) {
    const url = urlServicesRelease + '/cars/v4/leasing/' + idCar;
    return axios({
        title: 'Abrufen der Leasingdaten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setLeasing(idCar: Car['id'], data) {
    const url = urlServicesRelease + '/cars/v4/leasing/' + idCar;
    return axios({
        title: 'Ändern der Leasingdaten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function setFinanzierung(idCar: Car['id'], data) {
    const url = urlServicesRelease + '/cars/v4/finanzierung/' + idCar;
    return axios({
        title: 'Ändern der Finanzierungsdaten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function getEVAExport(data) {
    const url = urlExportRelease + '/eMymo/v1/task/lastExport';
    return axios({
        title: 'Abrufen des EVA-Exports',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function getTracking(idFirma: number, idCar: Car['id']) {
    const url = urlRelease + '/tracking/v2/car/' + idFirma + '/' + idCar;
    return axios({
        title: 'Abrufen der Mobile-Trackingdaten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

function getTrackingList(idFirma: number, idCars: Car['id'][], signal: AbortController['signal']) {
    const url = urlRelease + '/tracking/v1/cars/' + idFirma;
    return axios({
        title: 'Laden der Trackingdaten',
        method: 'post',
        url: url,
        data: idCars.join(','),
        headers: getAxiosHeader(),
        signal: signal,
    });
}

export function getDefaultTrackingCriteria(idCar: Car['id']) {
    const url = urlRelease + '/tracking/v2/criteria/' + idCar;
    return axios({
        title: 'Abrufen der Standard-Mobile-Trackingeinstellungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveTrackingCriteria(idCar: Car['id'], data) {
    const url = urlRelease + '/tracking/v1/criteria/save/' + idCar + '/1';
    return axios({
        title: 'Speichern der Mobile-Trackingeinstellungen',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function updateTracking(idCar: Car['id'], data) {
    const url = urlRelease + '/tracking/v2/msapim/car_' + idCar;
    return axios({
        title: 'Aktualisieren der Mobile-Trackingdaten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function copyCar(idCar: Car['id'], virtuell) {
    const url = urlServicesRelease + '/cars/v2/uweweb/copyCar';
    return axios({
        title: 'Duplizieren des Fahrzeugs',
        method: 'post',
        url: url,
        data: {
            idCar: idCar,
            toFirma: getIdFirma(),
            virtuell: virtuell ? 1 : 0,
        },
        headers: getAxiosHeader(),
    });
}

export function saveWLTP(idCar: Car['id'], consumptions: Car['consumptions']) {
    const url = urlServicesRelease + '/cars/v4/consumptions/' + idCar;
    return axios({
        title: 'Bearbeiten der WLTP-Werte',
        method: 'post',
        url: url,
        data: consumptions,
        headers: getAxiosHeader(),
    });
}
